<template>
  <div class="stax-form">
    <div class="d-flex gap-3">
      <TextField
        v-model="firstname"
        name="firstname"
        label="First Name"
        class="full-width mr-2"
        :validation-errors="v$.$errors"
      />
      <TextField
        v-model="lastname"
        name="lastname"
        label="Last Name"
        class="full-width mr-2"
        :validation-errors="v$.$errors"
      />
    </div>
    <div class="d-flex mb-2 gap-3">
      <SelectBox
        v-model="accountType"
        name="Account Type"
        label="Account Type"
        :options="accountTypes"
        placeholder="Select Account Type"
        :validation-errors="v$.$errors"
        class="mr-2 full-width"
      />
      <SelectBox
        v-model="holderType"
        name="Account Usage"
        label="Account Usage"
        :options="holderTypes"
        placeholder="Select Business or Personal"
        :validation-errors="v$.$errors"
        class="full-width"
      />
    </div>
    <div class="d-flex mb-2 gap-3">
      <TextField
        v-model="routingNumber"
        name="routingNumber"
        label="Routing Number"
        class="full-width mr-2"
        :validation-errors="v$.$errors"
      />
      <TextField
        v-model="accountNumber"
        name="accountNumber"
        label="Account Number"
        class="full-width"
        :validation-errors="v$.$errors"
      />
    </div>
    <div id="submit" class="submit-container"></div>
    <div
      v-if="config && config.card_savable && !autoSaveCard"
      class="d-flex flex-column mb-3"
    >
      <label class="mb-0 ml-2" style="font-size: 1.1em">
        <input v-model="savePaymentMethod" type="checkbox" />
        Save for future payments.
      </label>
      <span class="ml-2" style="font-size: 0.9em"
        >(Saving this payment method replaces any previously saved payment
        method on file.)</span
      >
    </div>
  </div>
</template>

<script>
import { handleStaxError } from "@/lib/helpers.js";

import TextField from "@/components/TextField.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import SelectBox from "../components/SelectBox.vue";
import usePaymentMethods from "./usePaymentMethods";

export default {
  components: {
    SelectBox,
    TextField
  },
  props: {
    autoSaveCard: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default: null
    }
  },
  setup() {
    const { paymentMethodsResponse } = usePaymentMethods();
    return {
      paymentMethodsResponse,
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      firstname: { required, $autoDirty: true },
      lastname: { required, $autoDirty: true },
      accountType: { required, $autoDirty: true },
      holderType: { required, $autoDirty: true },
      routingNumber: { required, $autoDirty: true },
      accountNumber: { required, $autoDirty: true }
    };
  },
  data() {
    return {
      accountTypes: [
        { label: "Checking", value: "checking" },
        { label: "Savings", value: "savings" }
      ],
      holderTypes: [
        { label: "Personal", value: "personal" },
        { label: "Business", value: "business" }
      ],
      staxJs: null,
      firstname: "",
      lastname: "",
      primaryAddress: {},
      routingNumber: "",
      accountNumber: "",
      accountType: "checking", // checking or savings
      holderType: "", // personal or business
      savePaymentMethod: false
    };
  },
  computed: {
    config() {
      return this.paymentMethodsResponse.find(
        (method) => method.key === "stax_ach"
      );
    }
  },
  mounted() {
    this.initForm();
  },
  methods: {
    async getData() {
      // clear out existing error dialogs for better UX
      window.notify.close();

      const validated = await this.v$.$validate();
      if (!validated) {
        throw new Error();
      }

      const details = {
        method: "bank",
        firstname: this.firstname,
        lastname: this.lastname,
        person_name: `${this.firstname} ${this.lastname}`,
        address_1: this.primaryAddress.street,
        address_city: this.primaryAddress.city,
        address_state: this.primaryAddress.state,
        address_zip: this.primaryAddress.zip,
        bank_type: this.accountType, // checking or savings
        bank_holder_type: this.holderType, // personal or business
        bank_account: this.accountNumber,
        bank_routing: this.routingNumber
      };
      try {
        const response = await this.staxJs.tokenize(details);
        return {
          transaction_type: "ach", // needed for PaymentMethodModal usage
          ach_first_name: this.firstname,
          ach_last_name: this.lastname,
          ach_token: response.id,
          ach_account_type: this.accountType,
          ach_account_number: this.accountNumber,
          ach_routing_number: this.routingNumber,
          ach_save_payment_method: this.savePaymentMethod || this.autoSaveCard
        };
      } catch (err) {
        const error = err; // make debuggable
        handleStaxError(this, error);
      }
    },
    initForm() {
      if (!this.config) {
        return;
      }

      if (this.customer) {
        this.firstname = this.customer.first_name;
        this.lastname = this.customer.last_name;
        this.primaryAddress =
          this.customer.primary_address ||
          this.customer.addresses.find((address) => address.primary);
      }

      this.staxJs = new StaxJs(this.config.web_id, {});
    }
  }
};
</script>

<style lang="scss" scoped>
.stax-form {
  .label {
    margin-bottom: 3px;
  }

  .submit-container {
    display: none;
  }
}
</style>
